import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import dayjs from "dayjs";

import { formatMoney } from "../../formatters";
import type { DataTableColDef } from "../DataTable";
import { AR_ACTION_OPTIONS, arActionLabel } from "./arActions";

const PLACEHOLDER = "—";

const dash = () => <span style={{ color: "#98A2B3" }}>{PLACEHOLDER}</span>;

// Aging buckets read cleaner as a dash when empty, matching the design.
function renderMoney(value: number | null | undefined) {
  if (value === null || value === undefined || value === 0) {
    return dash();
  }
  return formatMoney(value);
}

const moneyColumn = (
  field: keyof InsuranceArEntryMessage,
  headerName: string,
  width = 85,
): DataTableColDef<InsuranceArEntryMessage> => ({
  field,
  headerName,
  width,
  type: "number",
  renderCell: (params) => renderMoney(params.value as number),
});

const withDate = (text: string, at: string | null) =>
  at ? `${text} · ${dayjs(at).format("MMM D")}` : text;

const NO_ACTIVITY_LABEL = "Needs Action";

function getLatestActivityLabel(entry: InsuranceArEntryMessage): string {
  return entry.latestActivityAction
    ? arActionLabel(entry.latestActivityAction)
    : NO_ACTIVITY_LABEL;
}

function getLatestActivityLine1(entry: InsuranceArEntryMessage): {
  text: string;
  muted: boolean;
} {
  if (!entry.latestActivityAction) {
    return { text: NO_ACTIVITY_LABEL, muted: true };
  }
  const label = getLatestActivityLabel(entry);
  if (entry.plannedAction) {
    const at =
      entry.plannedActionType === "DENIAL_ACTION"
        ? entry.denialActionAt
        : entry.adjustmentActionAt;
    return { text: withDate(label, at), muted: false };
  }
  if (
    entry.isDeniseManaged &&
    entry.deniseStatus === "New" &&
    entry.lastAction
  ) {
    return { text: `Denise: ${label}`, muted: false };
  }
  return { text: withDate(label, entry.lastActionAt), muted: false };
}

function getStateLabel(entry: InsuranceArEntryMessage): string {
  if (entry.status === "WORK_COMPLETED") {
    return "Work completed";
  }
  switch (entry.nextStep) {
    case "WORK_IN_DENISE":
      return "Needs work";
    case "RECORD_WORK":
      return entry.denialAction === "CALL_PAYER"
        ? "Awaiting next step"
        : "No Wieldy denials";
    case "RECORD_CALL":
      return "Call payer — record the call";
    case "WAITING_ON_PAYER":
      return "Waiting on payer";
    case "RECORD_BALANCE_ADJUSTMENT":
      return "Ledger adjustment pending";
    case "CONFIRM_PENDING":
      return "Next step set — awaiting confirmation";
    case "NONE":
      return "";
    default: {
      // Compile-time exhaustiveness: a new nextStep value breaks the build here.
      const exhaustive: never = entry.nextStep;
      return exhaustive;
    }
  }
}

function LatestActivityCell({ entry }: { entry: InsuranceArEntryMessage }) {
  const line1 = getLatestActivityLine1(entry);
  const line2 = getStateLabel(entry);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <span
        style={
          line1.muted
            ? { fontSize: "12px", color: "#98A2B3" }
            : { fontSize: "12px", fontWeight: 600, color: "#101828" }
        }
      >
        {line1.text}
      </span>
      {line2 ? (
        <span style={{ fontSize: "12px", color: "#667085" }}>{line2}</span>
      ) : null}
    </Box>
  );
}

function renderChange(entry: InsuranceArEntryMessage) {
  if (entry.prevTotalAr === null || entry.prevTotalAr === undefined) {
    return dash();
  }
  const delta = entry.totalAr - entry.prevTotalAr;
  if (delta === 0) {
    return dash();
  }
  if (delta < 0) {
    return (
      <span style={{ color: "#027A48", fontWeight: 600 }}>
        ↓ {formatMoney(-delta)}
      </span>
    );
  }
  return (
    <span style={{ color: "#B42318", fontWeight: 600 }}>
      ↑ {formatMoney(delta)}
    </span>
  );
}

export interface InsuranceArNextStepHandlers {
  onWorkInDenise: (entry: InsuranceArEntryMessage) => void;
  onRecordWork: (entry: InsuranceArEntryMessage) => void;
  onRecordAdjustment: (entry: InsuranceArEntryMessage) => void;
  onConfirmPlan: (entry: InsuranceArEntryMessage) => void;
  onRecordCall: (entry: InsuranceArEntryMessage) => void;
  onEditNextStep: (entry: InsuranceArEntryMessage) => void;
}

function EditNextStepLink({ onClick }: { onClick: () => void }) {
  return (
    <Button
      size="small"
      variant="text"
      onClick={onClick}
      sx={{
        padding: 0,
        minWidth: 0,
        fontSize: "12px",
        textTransform: "none",
        textDecoration: "underline",
        color: "#667085",
      }}
    >
      edit next step
    </Button>
  );
}

function NextStepCell({
  entry,
  handlers,
  isConfirming,
}: {
  entry: InsuranceArEntryMessage;
  handlers: InsuranceArNextStepHandlers;
  isConfirming: boolean;
}) {
  switch (entry.nextStep) {
    case "WORK_IN_DENISE":
      return entry.matchingDenialKey ? (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handlers.onWorkInDenise(entry)}
        >
          Work in Denise
        </Button>
      ) : (
        dash()
      );
    case "RECORD_BALANCE_ADJUSTMENT":
      return (
        <Button
          size="small"
          variant="contained"
          onClick={() => handlers.onRecordAdjustment(entry)}
        >
          Record adjustment
        </Button>
      );
    case "RECORD_WORK":
      return (
        <Button
          size="small"
          variant="contained"
          onClick={() => handlers.onRecordWork(entry)}
        >
          Record work
        </Button>
      );
    case "RECORD_CALL":
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => handlers.onRecordCall(entry)}
          >
            Record call
          </Button>
          <EditNextStepLink onClick={() => handlers.onEditNextStep(entry)} />
        </Box>
      );
    case "CONFIRM_PENDING":
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={isConfirming}
            onClick={() => handlers.onConfirmPlan(entry)}
          >
            {isConfirming ? (
              <CircularProgress size={16} />
            ) : (
              `Confirm ${
                entry.plannedAction
                  ? arActionLabel(entry.plannedAction)
                  : "action"
              }`
            )}
          </Button>
          <EditNextStepLink onClick={() => handlers.onEditNextStep(entry)} />
        </Box>
      );
    case "NONE":
      return entry.status === "WORK_COMPLETED" ? (
        <EditNextStepLink onClick={() => handlers.onEditNextStep(entry)} />
      ) : (
        dash()
      );
    case "WAITING_ON_PAYER":
      return dash();
    default: {
      // Compile-time exhaustiveness: a new nextStep value breaks the build here.
      const exhaustive: never = entry.nextStep;
      return exhaustive ?? dash();
    }
  }
}

function NotesCell({
  entry,
  open,
  onToggle,
}: {
  entry: InsuranceArEntryMessage;
  open: boolean;
  onToggle: (entry: InsuranceArEntryMessage) => void;
}) {
  const count = entry.comments.length;
  const active = open || count > 0;
  return (
    <IconButton
      size="small"
      onClick={() => onToggle(entry)}
      aria-label={`Notes for ${entry.patientFullName ?? "this balance"}`}
      sx={{
        color: active ? "#2D6A52" : "#9CA3AF",
        backgroundColor: open ? "#E8F5EE" : "transparent",
      }}
    >
      <Badge
        badgeContent={count}
        invisible={count === 0}
        sx={{
          "& .MuiBadge-badge": {
            backgroundColor: "#2D6A52",
            color: "#FFFFFF",
            fontSize: "10px",
            height: "16px",
            minWidth: "16px",
          },
        }}
      >
        <ChatBubbleOutlineIcon fontSize="small" />
      </Badge>
    </IconButton>
  );
}

export function getInsuranceArColumns(
  devMode: boolean,
  onPatientClick: (entry: InsuranceArEntryMessage) => void,
  practiceNames: Record<string, string>,
  nextStepHandlers: InsuranceArNextStepHandlers,
  onToggleNotes: (entry: InsuranceArEntryMessage) => void,
  openNotesRowId: string | null,
  confirmingEntryIds: Set<string>,
): DataTableColDef<InsuranceArEntryMessage>[] {
  const columns: DataTableColDef<InsuranceArEntryMessage>[] = [
    {
      field: "practiceName",
      headerName: "Practice",
      width: 180,
      sortable: false,
      filterable: false,
      valueGetter: (_value, row) => practiceNames[row.practiceId] ?? "",
      renderCell: (params) => (params.value as string) || dash(),
    },
    {
      field: "patientFullName",
      headerName: "Patient",
      flex: 1.2,
      minWidth: 170,
      valueGetter: (_value, row) =>
        row.patientFullName ??
        [row.patientFirstName, row.patientLastName].filter(Boolean).join(" "),
      renderCell: (params) => {
        const name = (params.value as string) || PLACEHOLDER;
        const { patientKey } = params.row;
        if (!patientKey) {
          return <span style={{ textTransform: "uppercase" }}>{name}</span>;
        }
        return (
          <Button
            type="button"
            variant="text"
            onClick={() => onPatientClick(params.row)}
            aria-label={`View patient ${name}`}
            sx={{
              padding: 0,
              minWidth: 0,
              textTransform: "uppercase",
              textDecoration: "underline",
              color: "primary.main",
            }}
          >
            {name}
          </Button>
        );
      },
    },
    {
      field: "payerName",
      headerName: "Payer",
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) =>
        row.payerName ?? row.insuranceNameRaw ?? PLACEHOLDER,
    },
    {
      field: "channel",
      headerName: "Coverage",
      width: 130,
      type: "singleSelect",
      valueOptions: ["Denise", "Outside Denise"],
      renderCell: (params) => (params.value as string) || dash(),
    },
    {
      ...moneyColumn("totalAr", "Total A/R", 95),
      renderCell: (params) => (
        <span style={{ fontWeight: 600, color: "#101828" }}>
          {formatMoney(params.value as number)}
        </span>
      ),
    },
    moneyColumn("aging31To60", "30–60"),
    moneyColumn("aging61To90", "60–90"),
    moneyColumn("aging90Plus", "90+"),
    {
      field: "change",
      headerName: "Change",
      width: 90,
      type: "number",
      valueGetter: (_value, row) =>
        row.prevTotalAr === null || row.prevTotalAr === undefined
          ? null
          : row.totalAr - row.prevTotalAr,
      renderCell: (params) => renderChange(params.row),
    },
    {
      field: "daysOut",
      headerName: "Days Out",
      width: 80,
      type: "number",
      renderCell: (params) =>
        params.row.daysOut === null || params.row.daysOut === undefined
          ? dash()
          : params.row.daysOut,
    },
    {
      field: "deniseStatus",
      headerName: "Denise Status",
      width: 120,
      type: "singleSelect",
      // Raw values are what the search endpoint filters on; cells render the
      // display label from the payload.
      valueOptions: [
        { value: "NEW", label: "New" },
        { value: "WAITING_ON_PAYER", label: "Waiting on payer" },
        { value: "RESOLVED", label: "Resolved" },
      ],
      renderCell: (params) => (params.value as string) || dash(),
    },
    {
      field: "latestActivity",
      headerName: "Latest Activity",
      flex: 1.3,
      minWidth: 170,
      type: "singleSelect",
      valueOptions: [
        // Server-side sentinel for rows with no recorded activity.
        { value: "NEEDS_ACTION", label: NO_ACTIVITY_LABEL },
        ...AR_ACTION_OPTIONS,
      ],
      valueGetter: (_value, row) => row.latestActivityAction,
      renderCell: (params) => <LatestActivityCell entry={params.row} />,
    },
    {
      field: "nextStep",
      headerName: "Next Step",
      width: 200,
      type: "singleSelect",
      valueOptions: [
        { value: "RECORD_CALL", label: "Record call" },
        { value: "RECORD_WORK", label: "Record work" },
        { value: "RECORD_BALANCE_ADJUSTMENT", label: "Record adjustment" },
        { value: "CONFIRM_PENDING", label: "Awaiting confirmation" },
        { value: "WORK_IN_DENISE", label: "Work in Denise" },
        { value: "WAITING_ON_PAYER", label: "Waiting on payer" },
        { value: "NONE", label: "None" },
      ],
      renderCell: (params) => (
        <NextStepCell
          entry={params.row}
          handlers={nextStepHandlers}
          isConfirming={confirmingEntryIds.has(params.row.id)}
        />
      ),
    },
  ];

  if (devMode) {
    columns.push(
      {
        field: "lastPaymentDate",
        headerName: "Last Payment",
        width: 115,
        renderCell: (params) =>
          params.row.lastPaymentDate
            ? dayjs(params.row.lastPaymentDate).format("YYYY-MM-DD")
            : dash(),
      },
      {
        field: "patientKey",
        headerName: "Patient Key",
        width: 140,
        renderCell: (params) => params.row.patientKey ?? dash(),
      },
      {
        field: "payerId",
        headerName: "Payer ID",
        width: 140,
        renderCell: (params) => params.row.payerId ?? dash(),
      },
    );
  }

  columns.push({
    field: "notes",
    headerName: "Notes",
    width: 70,
    align: "center",
    sortable: false,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    disableExport: true,
    renderCell: (params) => (
      <NotesCell
        entry={params.row}
        open={params.row.id === openNotesRowId}
        onToggle={onToggleNotes}
      />
    ),
  });

  return columns;
}

export const DEFAULT_COLUMN_VISIBILITY = {
  practiceName: false,
  change: false,
  daysOut: false,
  deniseStatus: false,
  lastPaymentDate: false,
  patientKey: false,
  payerId: false,
};
